import { type ChangeEvent, type FormEvent, useState } from 'react';
import { api } from '../../../api/client';
import styles from './ExpenseForm.module.css';

interface ExpenseCategory {
  id: string;
  name: string;
}

interface Expense {
  id: string;
  categoryId: string;
  amount: number;
  description: string;
  date: string;
  receiptUrl: string | null;
  receiptType: string;
  notes: string | null;
}

interface ExpenseFormProps {
  expense: Expense | null;
  categories: ExpenseCategory[];
  onClose: () => void;
  onSuccess: () => void;
}

const RECEIPT_OPTIONS = [
  { value: 'none', label: 'Sin comprobante' },
  { value: 'invoice', label: 'Factura' },
  { value: 'receipt_note', label: 'Nota' },
];

export default function ExpenseForm({ expense, categories, onClose, onSuccess }: ExpenseFormProps) {
  const isEdit = !!expense;
  const [categoryId, setCategoryId] = useState(expense?.categoryId ?? '');
  const [amount, setAmount] = useState(expense ? String(expense.amount) : '');
  const [description, setDescription] = useState(expense?.description ?? '');
  const [date, setDate] = useState(
    expense?.date ? expense.date.slice(0, 10) : new Date().toISOString().slice(0, 10),
  );
  const [receiptType, setReceiptType] = useState(expense?.receiptType ?? 'none');
  const [notes, setNotes] = useState(expense?.notes ?? '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const body = {
        categoryId,
        amount: Number(amount),
        description,
        date,
        receiptType,
        notes: notes || undefined,
      };
      if (isEdit) {
        await api.put(`/api/expenses/${expense!.id}`, body);
      } else {
        await api.post('/api/expenses', body);
      }
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2>{isEdit ? 'Editar Gasto' : 'Nuevo Gasto'}</h2>
          <button onClick={onClose} className={styles.closeBtn}>
            &times;
          </button>
        </div>
        {error && <div className={styles.error}>{error}</div>}
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.field}>
            <label>Categoría</label>
            <select
              value={categoryId}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => setCategoryId(e.target.value)}
              required
            >
              <option value="">Seleccionar...</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.row}>
            <div className={styles.field}>
              <label>Monto ($)</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={amount}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setAmount(e.target.value)}
                required
              />
            </div>
            <div className={styles.field}>
              <label>Fecha</label>
              <input
                type="date"
                value={date}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setDate(e.target.value)}
                required
              />
            </div>
          </div>
          <div className={styles.field}>
            <label>Descripción</label>
            <input
              value={description}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
              required
            />
          </div>
          <div className={styles.field}>
            <label>Comprobante</label>
            <select
              value={receiptType}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => setReceiptType(e.target.value)}
            >
              {RECEIPT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.field}>
            <label>Notas (opcional)</label>
            <textarea
              value={notes}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
          <div className={styles.footer}>
            <button type="button" onClick={onClose} className={styles.cancelBtn}>
              Cancelar
            </button>
            <button type="submit" disabled={loading} className={styles.submitBtn}>
              {loading ? 'Guardando...' : isEdit ? 'Actualizar' : 'Crear Gasto'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
